import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, type TripFile } from "./api.ts";
import { heicToJpeg, isHeic, useDrawsHeic } from "./heic.ts";
import { ReportThis } from "./ReportThis.tsx";
import { useReadOnly } from "./readOnly.ts";
import { Button, Icon } from "./souvenir.ts";
import { fileHref } from "./viewing.ts";

// Files on a card: images as thumbnails that open full screen (a QR code fills the screen), PDFs (and
// anything a browser can't draw) as rows that open in a new tab. "Attach a file" takes one from this
// device, or one dropped on it; "Add a photo" a picture of the place, from the camera or the photo
// library on a phone, shown as its photo (Photos.tsx), not here. The bytes come from /api/files/<id>,
// which this browser keeps.

// On a shared trip's view link, the view's own address (viewing.ts).
const url = (f: TripFile) => fileHref(f.id);
export const drawable = (f: TripFile, drawsHeic: boolean | null) =>
  /^image\/(jpeg|png|webp)$/.test(f.type) || (f.type === "image/heic" && drawsHeic === true);
export const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_BYTES = MAX_FILE_BYTES;

export function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileList({
  files,
  trip,
  canRemove,
  onChanged,
}: {
  files: TripFile[];
  trip: string;
  canRemove: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState<TripFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const drawsHeic = useDrawsHeic();
  // Nothing comes off a read-only trip.
  const readOnly = useReadOnly();
  const removable = canRemove && !readOnly;
  if (files.length === 0) return null;
  const images = files.filter((f) => drawable(f, drawsHeic));
  const others = files.filter((f) => !drawable(f, drawsHeic));
  const remove = async (f: TripFile) => {
    setError(null);
    try {
      await api.removeFile(trip, f.id);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't remove it. Try again.");
    }
  };
  return (
    <div className="tm-files">
      {images.length > 0 && (
        <ul className="tm-file-thumbs" aria-label="Images">
          {images.map((f) => (
            <li key={f.id} className="tm-file-thumb">
              <button type="button" onClick={() => setOpen(f)} aria-label={`${f.name}, full screen`}>
                <Thumb file={f} />
              </button>
              {removable && (
                <button
                  type="button"
                  className="tm-file-remove"
                  onClick={() => remove(f)}
                  aria-label={`Remove ${f.name}`}
                >
                  <Icon name="close" size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {others.length > 0 && (
        <ul className="tm-file-rows" aria-label="Documents">
          {others.map((f) => (
            <li key={f.id} className="tm-file-row">
              <a className="tm-file-open" href={url(f)} target="_blank" rel="noreferrer">
                {f.as === "photo" ? (
                  // A photo this browser can't draw (HEIC, outside Safari): here, to download, saying why.
                  <>
                    <Icon name="photos" size={18} />
                    <span className="tm-file-text">
                      <span className="callout tm-file-name">{f.name}</span>
                      <span className="caption tm-muted">A HEIC photo this browser can't show</span>
                    </span>
                  </>
                ) : (
                  <>
                    <Icon name="book" size={18} />
                    <span className="callout tm-file-name">{f.name}</span>
                  </>
                )}
                <span className="caption tm-muted">{size(f.size)}</span>
              </a>
              {removable && (
                <Button
                  variant="quiet"
                  size="sm"
                  type="button"
                  onClick={() => remove(f)}
                  aria-label={`Remove ${f.name}`}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="caption tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
      {open && <Viewer file={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

// An image's thumbnail. One smaller than its box (a QR code) is scaled up crisp, not blurred.
function Thumb({ file }: { file: TripFile }) {
  const [small, setSmall] = useState(false);
  return (
    <img
      src={url(file)}
      alt=""
      loading="lazy"
      className={small ? "is-small" : ""}
      onLoad={(e) => setSmall(e.currentTarget.naturalWidth < e.currentTarget.clientWidth)}
    />
  );
}

// An image full screen, on black: for showing a QR code at a gate. Escape or Close returns. Drawn on the
// page itself, above everything, so no card or bar it was opened from can cover it.
export function Viewer({ file, onClose }: { file: TripFile; onClose: () => void }) {
  // A small image (a QR code) is scaled up crisp, pixel for pixel, so a scanner reads it.
  const [small, setSmall] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Only the viewer closes, not the card under it.
      e.stopImmediatePropagation();
      onClose();
    };
    addEventListener("keydown", onKey, true);
    return () => removeEventListener("keydown", onKey, true);
  }, [onClose]);
  return createPortal(
    <div className="tm-viewer" role="dialog" aria-modal="true" aria-label={file.name}>
      <img
        src={url(file)}
        alt={file.name}
        className={small ? "is-small" : ""}
        onLoad={(e) => setSmall(e.currentTarget.naturalWidth < innerWidth / 2)}
      />
      <button type="button" className="tm-viewer-close" onClick={onClose}>
        <Icon name="close" size={20} />
        <span>Close</span>
      </button>
    </div>,
    document.body,
  );
}

// Attaching from this device: a button, or a file dropped on it; and, where the card is a place, a stay
// or an option (`photos`), Add a photo: the camera or the photo library on a phone, any image on a
// computer. A HEIC photo is made a JPEG here first where the browser reads HEIC (Safari), so it shows
// everywhere. `invited`: the card was opened from an AI's upload link, for a file or a photo, so that
// one shows ready (and what's dropped is added as that). None on a read-only trip.
export function AttachFile(props: Parameters<typeof Attach>[0]) {
  return useReadOnly() ? null : <Attach {...props} />;
}

function Attach({
  trip,
  on,
  invited = null,
  photos = false,
  hidden = false,
  more = null,
  onChanged,
}: {
  trip: string;
  on: string;
  invited?: "file" | "photo" | null;
  photos?: boolean;
  // Put away while something takes its place (Add a link's form, CardLinks.tsx), kept so an upload goes on.
  hidden?: boolean;
  // More of the card's Files and links' buttons, on the same line after these (Add a link, Edit links).
  more?: ReactNode;
  onChanged: () => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const area = useRef<HTMLFieldSetElement>(null);
  const [busy, setBusy] = useState<"file" | "photo" | null>(null);
  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (invited) area.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [invited]);
  const send = async (list: FileList | null, as: "file" | "photo") => {
    const files = [...(list ?? [])];
    if (files.length === 0) return;
    setError(null);
    if (as === "photo") {
      const notImage = files.find((f) => !f.type.startsWith("image/") && !isHeic(f));
      if (notImage) {
        setError(`${notImage.name} isn't a photo. Attach it as a file instead.`);
        return;
      }
    }
    // A HEIC photo is checked once it's a JPEG (usually smaller).
    const tooBig = files.find((f) => f.size > MAX_BYTES && !(as === "photo" && isHeic(f)));
    if (tooBig) {
      setError(`${tooBig.name} is over 20 MB. Try a smaller one.`);
      return;
    }
    setBusy(as);
    try {
      for (const picked of files) {
        const f = as === "photo" && isHeic(picked) ? ((await heicToJpeg(picked)) ?? picked) : picked;
        if (f.size > MAX_BYTES) throw new Error(`${picked.name} is over 20 MB. Try a smaller one.`);
        await api.attachFile(trip, on, f, as);
      }
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't attach it. Try again.");
      // What went in before it is on the card.
      onChanged();
    } finally {
      setBusy(null);
      for (const input of [fileInput.current, photoInput.current]) if (input) input.value = "";
    }
  };
  return (
    // A drop area for the buttons inside it, which do the same for keyboards and screen readers.
    <fieldset
      ref={area}
      aria-label={invited === "photo" ? "Add a photo" : "Attach a file"}
      hidden={hidden}
      className={`tm-attach${invited ? " is-invited" : ""}${over ? " is-over" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        send(e.dataTransfer.files, invited === "photo" ? "photo" : "file");
      }}
    >
      <input
        ref={fileInput}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,.heic"
        multiple
        hidden
        aria-label="Choose a file to attach"
        onChange={(e) => send(e.target.files, "file")}
      />
      {photos && (
        // Any image: on a phone, the camera or the photo library (not the camera alone).
        <input
          ref={photoInput}
          type="file"
          accept="image/*,.heic,.heif"
          multiple
          hidden
          aria-label="Choose photos to add"
          onChange={(e) => send(e.target.files, "photo")}
        />
      )}
      <div className="tm-attach-buttons">
        {/* From an AI's link, only what it's for: the one button, ready. */}
        {invited !== "photo" && (
          <Button
            variant={invited === "file" ? "secondary" : "quiet"}
            size="sm"
            type="button"
            icon="plus"
            data-edit="file"
            disabled={busy !== null}
            onClick={() => fileInput.current?.click()}
          >
            {busy === "file" ? "Attaching…" : "Attach a file"}
          </Button>
        )}
        {photos && invited !== "file" && (
          <Button
            variant={invited === "photo" ? "secondary" : "quiet"}
            size="sm"
            type="button"
            icon="camera"
            data-edit="photo"
            disabled={busy !== null}
            onClick={() => photoInput.current?.click()}
          >
            {busy === "photo" ? "Adding…" : "Add a photo"}
          </Button>
        )}
        {!invited && more}
      </div>
      {invited === "file" && <p className="caption tm-muted">Or drop it here. A PDF or an image, up to 20 MB.</p>}
      {invited === "photo" && <p className="caption tm-muted">A picture of the place, up to 20 MB. Or drop it here.</p>}
      {error && (
        <p className="caption tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
    </fieldset>
  );
}
